import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Copy, TriangleAlert } from "lucide-react";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DescriptionItem,
  DescriptionList,
  EntityDrawer,
  PageState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  StatusDot,
  TabBar,
  splitMacros,
  useCopy,
  type BadgeVariant,
} from "@fox1ck-org/ui";
import { toast } from "@fox1ck-org/ui/toast";
import { StatusWord, StatusWordSource, type AffiliateNetwork, type NetworkNumbers, type NetworkStatusWord } from "@gen/tracker/v1/affiliate_network_pb";
import { ResourceKind } from "@gen/tracker/v1/common_pb";
import { ConversionStatus } from "@gen/tracker/v1/conversion_pb";
import { OfferStatus } from "@gen/tracker/v1/offer_pb";

import { useT, type TKey } from "@/lib/i18n";
import { DASH, formatInt } from "@/lib/format";
import { formatStamp } from "@/lib/utils";
import { carryPeriod } from "@/lib/route-tab";
import { useReportParams } from "@/lib/hooks/use-report-params";
import { useReportTimezone } from "@/lib/services/workspace-service";
import { statsRowToNumbers } from "@/lib/services/reporting-schema-service";
import { useNetworkStatuses, useUpdateAffiliateNetwork } from "@/lib/services/network-service";
import { useListOffers, useOfferNumbers } from "@/lib/services/offer-service";
import { RESULT_VIEW, resultKey, useListPostbacks } from "@/lib/services/postback-service";
import { History } from "@/components/offers/offer-card";
import { OFFER_STATUS_VIEW, OUR_STATUSES, OUR_STATUS_VIEW, modelCode, money } from "@/components/offers/labels";
import { toastFailure } from "@/lib/plan";

/**
 * The partner card (board PartnerCard), in an EntityDrawer over the list
 * (decision 08). Tabs are addresses — /library/networks/:id[/offers|
 * postbacks|history]. «Постбек и статусы»: the address to paste into the
 * partner's cabinet, and «Как читать статусы» — every word the partner sent
 * (ListNetworkStatuses), the unmapped ones first and flagged; saving it is
 * UpdateAffiliateNetwork with replay_newly_mapped, so the leads held on a
 * word mapped for the first time are applied again.
 *
 * «Отправить тестовый постбек» is not here: a test postback needs a test
 * click of a campaign, and lives on the campaign's «Ссылки и постбеки».
 */

export const PARTNER_TABS = ["postback", "offers", "postbacks", "history"] as const;
export type PartnerTab = (typeof PARTNER_TABS)[number];

const TAB_LABEL: Record<PartnerTab, TKey> = {
  postback: "u6.pc.tab.postback",
  offers: "u6.pc.tab.offers",
  postbacks: "u6.pc.tab.postbacks",
  history: "u6.card.tab.history",
};

const TONE: Record<string, BadgeVariant> = { ok: "ok", warn: "warn", danger: "danger", info: "info" };

export function PartnerCard({
  network,
  loading,
  error,
  numbers,
  tab,
  onTab,
  onClose,
  onEdit,
  onArchive,
}: {
  network?: AffiliateNetwork;
  loading: boolean;
  error?: unknown;
  numbers?: NetworkNumbers;
  tab: PartnerTab;
  onTab: (tab: PartnerTab) => void;
  onClose: () => void;
  onEdit?: () => void;
  onArchive?: () => void;
}) {
  const t = useT();
  return (
    <EntityDrawer
      open
      onOpenChange={(o) => !o && onClose()}
      title={network?.name ?? t("u6.card.loading")}
      description={network ? t("u6.pc.meta", { model: modelCode(network.defaultPayoutModel), currency: network.currency, offers: numbers?.offerCount ?? 0 }) : undefined}
      width="2xl"
      primaryAction={network && onEdit ? { label: t("u6.card.edit"), onClick: onEdit } : undefined}
      destructiveAction={network && onArchive ? { label: t("u6.common.archive"), onClick: onArchive } : undefined}
    >
      {error ? (
        <PageState mode="error" title={t("u6.pc.loadFailed")} error={error} />
      ) : loading || !network ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <PostbackBadge numbers={numbers} />
            {network.cabinetUrl ? (
              <a className="text-xs text-primary" href={network.cabinetUrl} target="_blank" rel="noreferrer noopener">
                {t("u6.pc.openCabinet")} ↗
              </a>
            ) : null}
          </div>
          <TabBar
            aria-label={t("u6.card.tabs")}
            value={tab}
            onValueChange={(v) => onTab(v as PartnerTab)}
            tabs={PARTNER_TABS.map((k) => ({ value: k, label: t(TAB_LABEL[k]), count: k === "offers" ? numbers?.offerCount : undefined }))}
          />
          {tab === "postback" ? (
            <>
              <PostbackAddress network={network} />
              <StatusTable network={network} />
            </>
          ) : null}
          {tab === "offers" ? <PartnerOffers network={network} limit={100} /> : null}
          {tab === "postbacks" ? <LastPostbacks networkId={network.id} limit={50} /> : null}
          {tab === "history" ? <History resourceId={network.id} kind={ResourceKind.AFFILIATE_NETWORK} /> : null}
          {tab === "postback" ? (
            <div className="flex flex-col gap-3">
              <Panel
                title={t("u6.pc.offers", { name: network.name })}
                right={
                  <button type="button" className="text-xs text-primary" onClick={() => onTab("offers")}>
                    {t("u6.pc.allOffers", { n: numbers?.offerCount ?? 0 })} →
                  </button>
                }
              >
                <PartnerOffers network={network} limit={4} />
              </Panel>
              <Panel title={t("u6.pc.lastPostbacks")} right={<JournalLink networkId={network.id} />}>
                <LastPostbacks networkId={network.id} limit={4} />
              </Panel>
            </div>
          ) : null}
        </div>
      )}
    </EntityDrawer>
  );
}

/** «приходит / нет 2 сут / не настроен» — the partner's postbacks at a glance. */
export function PostbackBadge({ numbers }: { numbers?: NetworkNumbers }) {
  const t = useT();
  if (!numbers) return null;
  if (!numbers.lastPostbackAt) return <Badge variant={numbers.silent ? "warn" : "neutral"} dot>{t(numbers.silent ? "u6.pb.silentNever" : "u6.pb.none")}</Badge>;
  const age = (Date.now() - Date.parse(numbers.lastPostbackAt)) / 1000;
  return (
    <Badge variant={numbers.silent ? "warn" : "ok"} dot>
      {numbers.silent ? t("u6.pb.silent", { age: ageWords(age, t) }) : t("u6.pb.arrives", { age: ageWords(age, t) })}
    </Badge>
  );
}

/** The list's «Постбек» cell (board PartnerList): a dot, the word, when. */
export function PostbackLine({ numbers }: { numbers?: NetworkNumbers }) {
  const t = useT();
  const tz = useReportTimezone();
  if (!numbers) return <span className="text-muted-foreground">{DASH}</span>;
  const at = numbers.lastPostbackAt;
  const age = at ? (Date.now() - Date.parse(at)) / 1000 : 0;
  const tone = !at ? (numbers.silent ? "warn" : "neutral") : numbers.silent ? "warn" : "ok";
  const word = !at ? t(numbers.silent ? "u6.pb.silentNever" : "u6.pb.none") : numbers.silent ? t("u6.pb.silentShort", { age: ageWords(age, t) }) : t("u6.pb.arrivesShort");
  return (
    <span className="inline-flex items-center gap-1.5">
      <StatusDot tone={tone} />
      <span>{word}</span>
      <span className="font-mono text-xs text-muted-foreground">{!at ? "" : numbers.silent ? formatStamp(at, tz) : t("u6.pb.ago", { age: ageWords(age, t) })}</span>
    </span>
  );
}

export function ageWords(seconds: number, t: ReturnType<typeof useT>): string {
  if (seconds < 90) return t("u6.age.sec", { n: Math.max(1, Math.round(seconds)) });
  if (seconds < 5400) return t("u6.age.min", { n: Math.round(seconds / 60) });
  if (seconds < 48 * 3600) return t("u6.age.hours", { n: Math.round(seconds / 3600) });
  return t("u6.age.days", { n: Math.round(seconds / 86400) });
}

function Panel({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) {
  return (
    <Card className="flex min-w-0 flex-col">
      <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 py-3">
        <CardTitle className="text-section-title">{title}</CardTitle>
        {right}
      </CardHeader>
      <CardContent className="flex flex-col gap-3 pb-4">{children}</CardContent>
    </Card>
  );
}

/**
 * The address the partner calls: the platform's postback host when it has
 * one, the path otherwise; the query carries the click under subid and the
 * payout under payout (the Edge's own aliases), the status under the
 * mapping's first status param, each with the partner's macro to fill.
 */
export function postbackTemplate(n: AffiliateNetwork): string {
  const base = n.postbackUrl || n.inboundPostbackUrl;
  const status = n.conversionMapping?.statusParams[0] ?? "status";
  return `${base}?subid={subid}&${status}={status}&payout={payout}`;
}

function PostbackAddress({ network }: { network: AffiliateNetwork }) {
  const t = useT();
  const url = postbackTemplate(network);
  const { copy, copied } = useCopy(url, { label: t("u6.pc.copy") });
  const marks = splitMacros(url, url.match(/\{[A-Za-z0-9_]+\}/g) ?? []);
  return (
    <Panel title={t("u6.pc.address", { name: network.name })}>
      <div className="flex items-stretch gap-2">
        <div className="min-w-0 flex-1 break-all rounded-md border border-border bg-surface-2 px-3 py-2 font-mono text-sm leading-relaxed">
          {marks.map((m, i) =>
            m.macro ? (
              <span key={i} className="rounded-sm bg-primary/10 px-0.5 text-primary">
                {m.text}
              </span>
            ) : (
              <span key={i}>{m.text}</span>
            ),
          )}
        </div>
        <Button
          type="button"
          variant="secondary"
          className="h-auto"
          onClick={() => {
            void copy().then(() => toast.success(t("u6.pc.copied")));
          }}
        >
          <Copy className="size-4" /> {copied ? t("u6.pc.copied") : t("u6.pc.copy")}
        </Button>
      </div>
      {!network.postbackUrl ? <span className="text-xs text-muted-foreground">{t("u6.pc.onCampaignDomain")}</span> : null}
      <DescriptionList>
        <DescriptionItem label={t("u6.pe.click")} value={<span className="font-mono">{"{subid}"} → {t("u6.pc.ourClick")}</span>} />
        <DescriptionItem label={t("u6.pe.status")} value={<span className="font-mono">{"{status}"}</span>} />
        <DescriptionItem label={t("u6.pe.payout")} value={t("u6.pc.payoutLine", { currency: network.currency, payout: "{payout}" })} />
      </DescriptionList>
      <span className="text-xs text-muted-foreground">{t("u6.pc.testHint")}</span>
    </Panel>
  );
}

const UNSET = "__unset";

/** The status table's rows as the screen edits them: word → our status ("" = not chosen). */
export function initialChoices(items: NetworkStatusWord[]): Record<string, string> {
  return Object.fromEntries(items.map((w) => [w.word, w.status ? String(w.status) : ""]));
}

/**
 * The mapping words to save: the network's own words as chosen, plus every
 * word whose meaning the person set or changed here. A word the global
 * dictionary already reads stays out unless it was changed. Pure.
 */
export function statusWordsToSave(items: NetworkStatusWord[], choices: Record<string, string>): StatusWord[] {
  const out: StatusWord[] = [];
  for (const w of items) {
    const chosen = choices[w.word] ?? "";
    if (!chosen) continue;
    const changed = chosen !== (w.status ? String(w.status) : "");
    if (w.source === StatusWordSource.NETWORK || changed) out.push(new StatusWord({ word: w.word, status: Number(chosen) as ConversionStatus }));
  }
  return out;
}

function StatusTable({ network }: { network: AffiliateNetwork }) {
  const t = useT();
  const words = useNetworkStatuses(network.id);
  const update = useUpdateAffiliateNetwork();
  const items = useMemo(() => words.data?.items ?? [], [words.data]);
  const [choices, setChoices] = useState<Record<string, string>>({});
  useEffect(() => setChoices(initialChoices(items)), [items]);
  const unmapped = items.filter((w) => w.source === StatusWordSource.UNMAPPED);
  const dirty = items.some((w) => (choices[w.word] ?? "") !== (w.status ? String(w.status) : ""));

  const save = async () => {
    const next = network.clone();
    const mapping = next.conversionMapping?.clone();
    if (!mapping) return;
    mapping.statuses = statusWordsToSave(items, choices);
    next.conversionMapping = mapping;
    try {
      await update.mutateAsync({ network: next, replayNewlyMapped: true });
      toast.success(t("u6.pc.st.saved"));
      void words.refetch();
    } catch (err) {
      toastFailure(err, t("u6.ed.saveFailed"));
    }
  };

  return (
    <Panel
      title={t("u6.pc.st.title", { name: network.name })}
      right={
        <Button type="button" variant="primary" size="sm" disabled={!dirty || update.isPending} onClick={() => void save()}>
          {t("u6.ed.save")}
        </Button>
      }
    >
      {unmapped.length > 0 ? (
        <Alert variant="warn">
          <TriangleAlert className="size-4" aria-hidden />
          <AlertDescription>{t("u6.pc.st.warn", { leads: formatInt(Number(words.data?.unmappedLeads ?? 0)) })}</AlertDescription>
        </Alert>
      ) : null}
      {words.isLoading ? <Skeleton className="h-32 w-full" /> : null}
      {!words.isLoading && items.length === 0 ? <span className="text-sm text-muted-foreground">{t("u6.pc.st.none")}</span> : null}
      {items.length > 0 ? (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-ink-3">
            <tr>
              <th className="py-1 text-left font-semibold">{t("u6.pc.st.theirs", { name: network.name })}</th>
              <th className="w-47.5 py-1 text-left font-semibold">{t("u6.pc.st.ours")}</th>
              <th className="w-22.5 py-1 text-right font-semibold">{t("u6.pc.st.leads")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((w) => {
              const isNew = w.source === StatusWordSource.UNMAPPED;
              const value = choices[w.word] ?? "";
              return (
                <tr key={w.word} className={isNew ? "border-t border-border bg-warn-soft/40" : "border-t border-border"}>
                  <td className="py-1.5">
                    <span className="font-mono">{w.word}</span> {isNew ? <Badge variant="warn">{t("u6.pc.st.new")}</Badge> : null}
                    {w.source === StatusWordSource.GLOBAL ? <span className="ml-1 text-xs text-muted-foreground">{t("u6.pc.st.dict")}</span> : null}
                  </td>
                  <td className="py-1">
                    <Select value={value || UNSET} onValueChange={(v) => setChoices((c) => ({ ...c, [w.word]: v === UNSET ? "" : v }))}>
                      <SelectTrigger aria-label={t("u6.pc.st.ours")} className={isNew && !value ? "h-7 border-warn" : "h-7"}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={UNSET}>{t("u6.common.pick")}</SelectItem>
                        {OUR_STATUSES.map((s) => (
                          <SelectItem key={s} value={String(s)}>
                            {t(OUR_STATUS_VIEW[s].label)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{formatInt(Number(w.leads) || undefined)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
    </Panel>
  );
}

function PartnerOffers({ network, limit }: { network: AffiliateNetwork; limit: number }) {
  const t = useT();
  const p = useReportParams();
  const { search } = useLocation();
  const list = useListOffers({ networkId: network.id, limit, statuses: [OfferStatus.ACTIVE, OfferStatus.PAUSED, OfferStatus.CAPPED] });
  const offers = list.data?.items ?? [];
  const numbers = useOfferNumbers({ ids: offers.map((o) => o.id), range: { from: p.from, to: p.to }, timezone: p.tz, basis: p.basis });
  const byId = new Map((numbers.data?.items ?? []).map((n) => [n.offerId, n]));
  if (list.isLoading) return <Skeleton className="h-20 w-full" />;
  if (offers.length === 0) return <span className="text-sm text-muted-foreground">{t("u6.pc.noOffers")}</span>;
  return (
    <table className="w-full table-fixed text-sm">
      <thead className="text-xs uppercase tracking-wide text-ink-3">
        <tr>
          <th className="py-1 text-left font-semibold">{t("u6.col.offer")}</th>
          <th className="w-24 py-1 text-right font-semibold">{t("u6.col.payout")}</th>
          <th className="w-16 py-1 text-right font-semibold">{t("u6.col.leads")}</th>
          <th className="w-36 py-1 pl-2 text-left font-semibold">{t("u6.col.status")}</th>
        </tr>
      </thead>
      <tbody>
        {offers.map((o) => {
          const n = byId.get(o.id);
          const v = n?.cap?.reached && o.status === OfferStatus.ACTIVE ? OFFER_STATUS_VIEW[OfferStatus.CAPPED] : OFFER_STATUS_VIEW[o.status];
          return (
            <tr key={o.id} className="border-t border-border first:border-0">
              <td className="truncate py-1.5">
                <Link className="hover:text-primary" title={o.name} to={`/library/offers/${o.id}${carryPeriod(search)}`}>
                  {o.name}
                </Link>
              </td>
              <td className="py-1.5 text-right tabular-nums">{money(o.payout?.amount?.amount, o.payout?.amount?.currency)}</td>
              <td className="py-1.5 text-right tabular-nums">{formatInt(statsRowToNumbers(n?.stats).leads || undefined)}</td>
              <td className="py-1.5 pl-2">
                <Badge variant={v.tone} dot>
                  {t(v.label)}
                </Badge>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function JournalLink({ networkId }: { networkId: string }) {
  const t = useT();
  const { search } = useLocation();
  const carried = carryPeriod(search);
  return (
    <Link className="text-xs text-primary" to={`/journal/postbacks${carried}${carried ? "&" : "?"}network=${networkId}`}>
      {t("u6.pc.toJournal")} →
    </Link>
  );
}

function LastPostbacks({ networkId, limit }: { networkId: string; limit: number }) {
  const t = useT();
  const p = useReportParams();
  const q = useListPostbacks({ from: p.from, to: p.to, timezone: p.tz, networkId, limit, offset: 0 });
  const items = q.data?.items ?? [];
  if (q.isLoading) return <Skeleton className="h-20 w-full" />;
  if (items.length === 0) return <span className="text-sm text-muted-foreground">{t("u6.pc.noPostbacks")}</span>;
  return (
    <table className="w-full table-fixed text-sm">
      <thead className="text-xs uppercase tracking-wide text-ink-3">
        <tr>
          <th className="w-28 py-1 text-left font-semibold">{t("u6.pc.pb.at")}</th>
          <th className="py-1 text-left font-semibold">{t("u6.pc.pb.click")}</th>
          <th className="py-1 text-left font-semibold">{t("u6.pc.pb.status")}</th>
          <th className="w-36 py-1 text-left font-semibold">{t("u6.pc.pb.result")}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((r) => {
          const key = resultKey(r.result);
          const view = key ? RESULT_VIEW[key] : undefined;
          return (
            <tr key={r.eventId} className="border-t border-border first:border-0">
              <td className="py-1.5 tabular-nums text-muted-foreground">{formatStamp(r.receivedAt, p.tz)}</td>
              <td className="truncate py-1.5 font-mono text-xs">{r.clickId ? `${r.clickId.slice(0, 6)}…${r.clickId.slice(-3)}` : DASH}</td>
              <td className="truncate py-1.5 font-mono text-xs">{r.rawStatus || DASH}</td>
              <td className="py-1.5">{view ? <Badge variant={view.tone ? (TONE[view.tone] ?? "neutral") : "neutral"}>{t(view.label)}</Badge> : DASH}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
